// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ExpandMoreRounded } from '@/constants/hardfin-standard-icons';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Typography,
} from '@mui/material';
import type { ReactNode } from 'react';

// The always-visible header for a currency section: the ISO code on the left
// and an optional headline metric (e.g. the hub's "Ready to invoice $0.00") on
// the right, before the expand chevron.
function CurrencySectionHeader(props: {
  currencyCode: IsoCurrencyCode;
  header?: ReactNode;
  chip?: ReactNode;
  prominent?: boolean;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ width: '100%' }}
    >
      <Typography
        variant={props.prominent ? 'subtitle1' : 'subtitle2'}
        color={props.prominent ? 'text.primary' : 'text.secondary'}
        sx={{ fontWeight: props.prominent ? 600 : undefined }}
      >
        {props.currencyCode}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={1}>
        {props.header}
        {props.chip}
      </Stack>
    </Stack>
  );
}

// A single collapsible per-currency section on the billing reports. The header
// shows the ISO code (plus an optional headline metric); the collapsible body
// holds that currency's cards. Rendered transparent so it blends into the page
// rather than reading as a nested card.
function CurrencySectionAccordion(props: {
  currencyCode: IsoCurrencyCode;
  header?: ReactNode;
  chip?: ReactNode;
  prominent?: boolean;
  defaultExpanded: boolean;
  expanded?: boolean;
  onToggle?: (currencyCode: IsoCurrencyCode) => void;
  domId?: string;
  children: ReactNode;
}) {
  const isControlled = props.expanded !== undefined;
  return (
    <Accordion
      id={props.domId}
      disableGutters
      elevation={0}
      {...(isControlled
        ? {
            expanded: props.expanded,
            onChange: () => props.onToggle?.(props.currencyCode),
          }
        : { defaultExpanded: props.defaultExpanded })}
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        '&::before': { display: 'none' },
        '&:not(:last-of-type)': { mb: 1.5 },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreRounded fontSize="small" />}
        sx={{
          bgcolor: props.prominent ? 'background.paper' : 'action.hover',
          flexDirection: 'row-reverse',
          gap: 1,
          ...(props.prominent
            ? { borderBottom: 1, borderColor: 'divider' }
            : {}),
        }}
      >
        <CurrencySectionHeader
          currencyCode={props.currencyCode}
          header={props.header}
          chip={props.chip}
          prominent={props.prominent}
        />
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 2 }}>{props.children}</AccordionDetails>
    </Accordion>
  );
}

// Stacks per-currency sections for a billing report. With a single currency the
// body renders flat under a plain ISO header; with more than one, each currency
// becomes a collapsible accordion and the first — the highest-value bucket, as
// ordered by the backend — is expanded by default. `renderBucket` returns that
// currency's collapsible body; `renderHeader` (optional) adds a headline metric
// to the always-visible currency header. A loaded-but-empty set (zero invoices
// in scope) shows a muted line rather than a blank gap; `emptyMessage`
// overrides the default copy.
export function PerCurrencySections<
  T extends { isoCode: IsoCurrencyCode },
>(props: {
  buckets: T[];
  renderBucket: (bucket: T) => ReactNode;
  renderHeader?: (bucket: T) => ReactNode;
  renderHeaderChip?: (bucket: T) => ReactNode;
  emptyMessage?: string;
  // Bold card-style header instead of the subtle billing style.
  prominentHeader?: boolean;
  // Render an accordion even for a single bucket (default: a single bucket renders flat).
  alwaysAccordion?: boolean;
  // Uncontrolled default expansion per section; defaults to "first only".
  defaultExpanded?: (currencyCode: string, index: number) => boolean;
  // Controlled expansion: exactly these currencyCodes are open. Pair with onToggleSection.
  expandedCurrencyCodes?: ReadonlySet<string>;
  onToggleSection?: (currencyCode: IsoCurrencyCode) => void;
  // Prefix for each accordion's DOM id: `${prefix}-${isoCode}`.
  sectionIdPrefix?: string;
}) {
  if (props.buckets.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {props.emptyMessage ?? 'No data for the selected filters'}
      </Typography>
    );
  }
  if (props.buckets.length === 1 && props.alwaysAccordion !== true) {
    const bucket = props.buckets[0];
    return (
      <Box>
        <Box sx={{ mb: 1 }}>
          <CurrencySectionHeader
            currencyCode={bucket.isoCode}
            header={props.renderHeader?.(bucket)}
            chip={props.renderHeaderChip?.(bucket)}
            prominent={props.prominentHeader}
          />
        </Box>
        {props.renderBucket(bucket)}
      </Box>
    );
  }
  const isControlled = props.expandedCurrencyCodes !== undefined;
  return (
    <>
      {props.buckets.map((bucket, index) => (
        <CurrencySectionAccordion
          key={bucket.isoCode}
          currencyCode={bucket.isoCode}
          header={props.renderHeader?.(bucket)}
          chip={props.renderHeaderChip?.(bucket)}
          prominent={props.prominentHeader}
          defaultExpanded={
            props.defaultExpanded
              ? props.defaultExpanded(bucket.isoCode, index)
              : index === 0
          }
          expanded={
            isControlled
              ? props.expandedCurrencyCodes?.has(bucket.isoCode)
              : undefined
          }
          onToggle={props.onToggleSection}
          domId={
            props.sectionIdPrefix
              ? `${props.sectionIdPrefix}-${bucket.isoCode}`
              : undefined
          }
        >
          {props.renderBucket(bucket)}
        </CurrencySectionAccordion>
      ))}
    </>
  );
}
